import { observer } from 'mobx-react-lite';
import { useStore } from '../../../../hooks/useStore';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { BenefitButton } from '../../../../lib/components/atomic/dataDisplay/benefitButton';
import { Benefit } from '@pxtra-client-lib-legacy/types';
import { ArrowForwardIconSvg, LaunchIcon } from '../../../../lib/icons';
import { useTranslation } from 'react-i18next';
import { useStepper } from '../../partials/actionStepperPartial';
import { enqueueSnackbar } from 'notistack';
import { useCreateBenefitSet } from '../hooks/useCreateBenefitSet';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { Button } from '../../../../lib/components/atomic/button';
import { CardOfferPanel } from '../../../../lib/components/compound/pxtraCardOffer';
import { CardOnboardingNotice } from '../../../../lib/components/compound/pxtraCardOnboarding/onboardingNotice';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback/alert';
import { useCardOfferApplies } from '../hooks/useCardOfferApplies';

export const BenefitSelection = observer(() => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { newBenefitSet } = useCreateBenefitSet();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const [bookedBenefits, setBookedBenefits] = useState<Benefit[]>([]);
  const [buttonWidth, setButtonWidth] = useState(220);

  const containerRef = useRef<HTMLDivElement>(null);
  const params = useParams();
  const cardOfferApplies = useCardOfferApplies();
  const showCardOffer = cardOfferApplies && newBenefitSet.selectedBenefits.includes('sachbezug');
  const sachbezugLocked = newBenefitSet.sachbezugLocked(
    cardOfferApplies,
    params.type === 'edit_benefit_set'
  );

  const handleBenefitToggle = (benefit: Benefit) => {
    if (newBenefitSet.selectedBenefits.includes(benefit)) {
      newBenefitSet['selectedBenefits'] = newBenefitSet.selectedBenefits.filter(
        (b) => b !== benefit
      );
    } else {
      newBenefitSet['selectedBenefits'] = [...newBenefitSet.selectedBenefits, benefit];
    }
  };

  useEffect(() => {
    setNextButtonConfig({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        if (
          newBenefitSet.selectedBenefits.length === 0 &&
          newBenefitSet.assignmentContext !== 'emptySet'
        ) {
          enqueueSnackbar(
            <div className="flex flex-col gap-y-1">
              <div>{t('common:actionRequired')}</div>
              <div className="font-normal !text-grey-700">
                {t('benefitSet:emptySelectionError')}
              </div>
            </div>,
            {
              variant: 'error'
            }
          );
        } else {
          setActiveStep(activeStep + 1);
        }
      }
    });
  }, []);

  useEffect(() => {
    const calculateWidth = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      const gap = 32;
      const minWidth = 198;
      const maxWidth = 242;
      const borderWidth = 2;

      const buttonsPerRow = Math.floor((containerWidth + gap) / (minWidth + gap));
      const availableSpace = containerWidth - (buttonsPerRow - 1) * gap;
      const totalBorderWidth = buttonsPerRow * borderWidth;

      const calculatedWidth = Math.min(
        maxWidth,
        (availableSpace - totalBorderWidth) / buttonsPerRow
      );

      setButtonWidth(calculatedWidth);
    };

    calculateWidth();
    window.addEventListener('resize', calculateWidth);
    return () => window.removeEventListener('resize', calculateWidth);
  }, [containerRef.current?.offsetWidth]);

  useEffect(() => {
    setBookedBenefits(companyStore.bookedBenefits as Benefit[]);
  }, [companyStore.bookedBenefits]);

  return (
    <>
      {cardOfferApplies && <CardOnboardingNotice />}
      <div ref={containerRef} className="flex flex-wrap gap-8 mb-3">
        {bookedBenefits.map((benefit) => (
          <BenefitButton
            key={benefit}
            benefit={benefit}
            disabled={
              newBenefitSet.assignmentContext == 'emptySet' ||
              (benefit === 'sachbezug' && sachbezugLocked)
            }
            isSelected={newBenefitSet.selectedBenefits.includes(benefit)}
            width={buttonWidth}
            onToggle={handleBenefitToggle}
          />
        ))}
      </div>
      {showCardOffer && (
        <div className="mt-8 flex flex-col gap-y-4">
          {sachbezugLocked && newBenefitSet.cardOfferBaseline.card_enabled && (
            <FeedbackAlert severity="info" description={t('benefitSet:cardOfferSachbezugLocked')} />
          )}
          <CardOfferPanel disabled={newBenefitSet.assignmentContext === 'emptySet'} />
        </div>
      )}
      <Drawer
        label={t('benefitSet:selectionDrawerTitle')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('benefitSet:selectionDrawerTexts', { returnObjects: true })} />
          <Button
            className="w-full"
            variant="secondary"
            leadingIcon={<LaunchIcon />}
            onClick={() =>
              window.open(
                'https://support.pxtra.de/platform/welche-benefits-werden-%C3%BCber-pxtra-angeboten',
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            {t('common:toHelpCenter')}
          </Button>
        </div>
      </Drawer>
    </>
  );
});
